import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { formatPrice } from '@spotahome/soyuz-currency';

import {
  FREQUENCY_MONTHLY,
  PAYMENT_TO_LANDLORD,
  APPLY_ON_INCLUDED,
  COST_CATEGORY_BILLS,
  COST_CATEGORY_MONTHLY_RENT
} from '../../constants/costs';

import { formatMonthlyPaymentDate } from '../utils/dateUtils';

import OffersLabel from '../OffersLabel';

import BillsLabelIcon from '../icons/BillsLabelIcon';

import { ListingCostsShape } from '../../constants/shapes';

import { capitalizeFirstLetter } from '../utils/stringUtils';

import PaymentTimelineCard from './PaymentTimelineCard';
import PaymentTimeLineItem from './PaymentTimelineItem';

import { getUnit, isEstimatedCost } from './costHelpers';

import styles from './PaymentTimelineCard.module.scss';

import {
  DuringStaySectionTitle,
  formatCostTitle,
  getBillValue,
  getBillValueSubtitle,
  getCostSubtitle,
  getTooltipText
} from './duringStayUtils';

const PERMANENT_CONTRACT_REST_OF_MONTHS_INDEX = 2;

const isSomeBillsIncluded = billsCosts =>
  !!billsCosts.length && billsCosts.some(c => c.applyOn === APPLY_ON_INCLUDED);

const isAllBillsIncluded = billsCosts =>
  !!billsCosts.length && billsCosts.every(c => c.applyOn === APPLY_ON_INCLUDED);

const getTags = ({ billsCosts, offers, appliedOfferValue }) => {
  const isSomeBills = isSomeBillsIncluded(billsCosts);
  const isAllBills = isAllBillsIncluded(billsCosts);
  const tags = [
    {
      tag: () => (
        <OffersLabel
          offers={offers}
          appliedOfferValue={
            appliedOfferValue > 0 ? `${appliedOfferValue}%` : null
          }
          clickable
          showIcon
          size="big"
        />
      ),
      applicable: offers?.length > 0 || appliedOfferValue > 0
    },
    {
      tag: {
        label: isAllBills
          ? trans('listing.labels.bills.all.title')
          : trans('listing.labels.bills.some.title'),
        renderLeadingIcon: () => <BillsLabelIcon size="24" />,
        isSpaced: true,
        size: 'big'
      },
      applicable: isSomeBills || isAllBills
    }
  ];

  return tags.filter(t => t.applicable !== false).map(t => t.tag);
};

const DuringStay = ({
  offers = [],
  appliedOfferValue = null,
  isPricingCalculated,
  listingMonthlyPrice = null,
  costs = {},
  currencyCode,
  isCollapsed = true,
  contractDurationType = 'temporal'
}) => {
  const isPermanentContract = contractDurationType === 'permanent';
  const duringStayCosts = costs?.costs || [];
  const totalValue = isPricingCalculated
    ? formatPrice(costs?.total, currencyCode)
    : listingMonthlyPrice;

  const totalValueWithoutOffer =
    isPricingCalculated && costs?.totalWithoutOffer
      ? formatPrice(costs?.totalWithoutOffer, currencyCode)
      : null;

  const monthlyRentCosts = duringStayCosts.filter(
    c => c.category === COST_CATEGORY_MONTHLY_RENT
  );

  const allMonthsIncluded =
    monthlyRentCosts.length > 0 &&
    monthlyRentCosts.every(c => c.applyOn === APPLY_ON_INCLUDED);

  const billsCosts = duringStayCosts.filter(
    c => c.category === COST_CATEGORY_BILLS
  );

  const otherCosts = duringStayCosts.filter(
    c =>
      c.category !== COST_CATEGORY_MONTHLY_RENT &&
      c.category !== COST_CATEGORY_BILLS
  );

  return (
    <PaymentTimelineCard
      title={trans('payment.timeline.section.during_stay.title')}
      value={
        allMonthsIncluded
          ? totalValue
          : trans('price.per.month', { price: totalValue })
      }
      valueWithoutOffer={
        totalValueWithoutOffer > 0
          ? trans('price.per.month', {
              price: totalValueWithoutOffer
            })
          : null
      }
      isCollapsed={isCollapsed}
      collapsible={isPricingCalculated}
      tags={getTags({ offers, billsCosts, appliedOfferValue })}
      dataTest="spotahomeBookingPaymentInfo.monthly"
    >
      {monthlyRentCosts.length > 0 && (
        <>
          <DuringStaySectionTitle
            frequency={FREQUENCY_MONTHLY}
            payTo={monthlyRentCosts[0]?.payTo || PAYMENT_TO_LANDLORD}
            title={trans('listing.pricing.monthly-rent')}
            titleClassName={styles['payment-timeline-card__section-title']}
          />
          {monthlyRentCosts.map((cost, index) => {
            const isRestOfMonths =
              isPermanentContract &&
              index === PERMANENT_CONTRACT_REST_OF_MONTHS_INDEX;
            return (
              <PaymentTimeLineItem
                key={`${cost.type}-${cost.paymentDate}`}
                value={cost.value}
                valueWithoutOffer={cost.valueWithoutOffer}
                currencyCode={currencyCode}
                title={
                  isRestOfMonths
                    ? trans('landlordPaymentsTimeline.stay.restOfMonths')
                    : capitalizeFirstLetter(
                        formatMonthlyPaymentDate(cost.paymentDate)
                      )
                }
                subtitle={getCostSubtitle(cost)}
                included={cost.applyOn === APPLY_ON_INCLUDED && index === 0}
                includedCopy={trans(
                  'spotahomeBookingPaymentInfo.downPaymentFirstMonthStrategy.firstMonthCovered'
                )}
                tooltipText={getTooltipText(cost, index)}
                dataTest={
                  isRestOfMonths
                    ? 'landlordPaymentsTimeline.stay.monthRent.restOfMonths'
                    : `landlordPaymentsTimeline.stay.monthRent.${index}`
                }
              />
            );
          })}
        </>
      )}

      {billsCosts.length > 0 && (
        <>
          <DuringStaySectionTitle
            payTo={billsCosts[0]?.payTo || PAYMENT_TO_LANDLORD}
            title={trans('listing.bills.title')}
            titleClassName={styles['payment-timeline-card__section-title']}
          />
          {billsCosts.map(cost => (
            <PaymentTimeLineItem
              key={`${cost.type}`}
              value={getBillValue(cost)}
              valueSubtitle={getBillValueSubtitle(cost, currencyCode)}
              currencyCode={currencyCode}
              title={formatCostTitle(cost.type)}
              subtitle={getCostSubtitle(cost)}
              included={cost.applyOn === APPLY_ON_INCLUDED}
            />
          ))}
        </>
      )}

      {otherCosts.length > 0 && (
        <>
          <DuringStaySectionTitle
            title={trans('payment.timeline.other.costs')}
            titleClassName={styles['payment-timeline-card__section-title']}
          />
          {otherCosts.map(cost => (
            <PaymentTimeLineItem
              key={`${cost.type}-${cost.paymentDate}`}
              title={
                cost.name ?? trans(`payment.timeline.cost.label.${cost.type}`)
              }
              value={cost.value}
              currencyCode={currencyCode}
              subtitle={getCostSubtitle(cost)}
              tooltipText={cost.description || null}
              included={cost.applyOn === APPLY_ON_INCLUDED}
              unit={getUnit(cost.applyOn)}
              valueSubtitle={
                isEstimatedCost(cost.applyOn)
                  ? trans('payment.timeline.estimated-cost')
                  : null
              }
            />
          ))}
        </>
      )}
    </PaymentTimelineCard>
  );
};

DuringStay.propTypes = {
  offers: PropTypes.arrayOf(PropTypes.shape({})),
  appliedOfferValue: PropTypes.number,
  isPricingCalculated: PropTypes.bool.isRequired,
  listingMonthlyPrice: PropTypes.string,
  costs: ListingCostsShape,
  currencyCode: PropTypes.string.isRequired,
  isCollapsed: PropTypes.bool,
  contractDurationType: PropTypes.string
};

export default DuringStay;
